/* eslint-disable unicorn/filename-case */
import type { AxiosRequestConfig } from 'axios';
import { injectable } from 'inversify';
import type { DocumentId, DocumentRevision } from '../types';
import { BaseRepository } from './Base.repository';
import { ConflictError, NotModifiedError, CustomError } from './errors';
import type { BaseDocumentChangeModel, GenericModel } from './models';
import type {
  GetListOptions,
  GetListReturnType,
  PaginationResponse,
  UpdateManyResponse,
  UpdateResponse,
} from './types';
import type { Migration } from './interfaces';
import { ErrorCodes } from './literals';

@injectable()
export class BaseCRUDRepository<TModel extends GenericModel = GenericModel> extends BaseRepository {
  protected migrator?: Migration<TModel>;

  async duplicateRoot(id: DocumentId): Promise<TModel> {
    const { data } = await this.httpClient.post<TModel>(`/workspaces/${id}/copy`);

    return data;
  }

  async update(model: TModel, config?: AxiosRequestConfig): Promise<TModel> {
    const { headers } = await this.httpClient.put<null, UpdateResponse<null>>(
      `/${model._id}`,
      model,
      {
        ...config,
        headers: {
          'If-Match': `"${model._rev}"`,
        },
      },
    );

    return {
      ...model,
      _rev: Number.parseInt(JSON.parse(headers.etag), 10) as DocumentRevision,
    };
  }

  async updateMany(
    models: TModel[],
    config?: AxiosRequestConfig,
  ): Promise<{ model?: TModel; id: DocumentId; error?: CustomError | unknown; ok: boolean }[]> {
    const { data } = await this.httpClient.post<UpdateManyResponse[]>('/update', models, config);
    return models.map(model => {
      const response = data.find(({ id }) => id === model._id);

      if (!response) {
        return {
          ok: false,
          id: model._id,
          error: new NotModifiedError(),
        };
      }

      if (response.ok) {
        return {
          ok: true,
          id: model._id,
          model: { ...model, _rev: response.rev },
        };
      }

      if (response.reason === 'conflict') {
        return {
          ok: false,
          id: model._id,
          model,
          error: new ConflictError(),
        };
      }

      if (response.reason === 'onBeforeDocUpdatedFailed' && response.error) {
        return {
          ok: false,
          id: model._id,
          model,
          error: response.error,
        };
      }

      return {
        ok: false,
        id: model._id,
        model,
        error: new CustomError(response.err ?? 'Something went wrong!', ErrorCodes.serverError),
      };
    });
  }

  async create(node: Partial<TModel>): Promise<TModel> {
    const model = this.migrator ? await this.migrator.apply(node, true) : node;

    const { data } = await this.httpClient.post<TModel>('/', model);

    return data;
  }

  async remove(id: DocumentId, rev: DocumentRevision): Promise<void> {
    const { data } = await this.httpClient.delete<void>(`/${id}`, {
      headers: {
        'If-Match': `"${rev}"`,
      },
    });

    return data;
  }

  async get(id: DocumentId, config?: AxiosRequestConfig): Promise<TModel> {
    const document = await this.httpClient.get<TModel>(`/${id}`, config).then(({ data }) => data);
    if (this.migrator) {
      return this.migrator.apply(document);
    }

    return document;
  }

  async getMany(ids: DocumentId[], config?: AxiosRequestConfig) {
    const { data } = await this.httpClient.post<TModel[]>('/find', ids, config);
    const documents = data ?? [];

    if (this.migrator) {
      return Promise.all(documents.map(document => this.migrator!.apply(document)));
    }

    return documents;
  }

  /**
   * Method returns list of all entities with pagination if request includes param 'useListResponse=true'
   * Otherwise response with all entities (not recommended)
   */
  async getList<D extends GetListOptions>(
    options?: D,
  ): Promise<PaginationResponse<TModel> | TModel[]> {
    return this.httpClient
      .get<GetListReturnType<D, TModel>>('/', { params: { limit: 150, ...options?.params } })
      .then(({ data }) => data);
  }

  getChanges(id: DocumentId, since: DocumentRevision) {
    const options = {
      params: {
        since,
      },
    };

    return this.httpClient
      .get<BaseDocumentChangeModel<TModel>[] | null>(`${id}/changes`, options)
      .then(({ data }) => data ?? []);
  }
}
